/**
 * What arranging from the tune did, said along the bottom of the board: the twin of
 * `ArrangeReading` for the other place arranging can start.
 *
 * Starting from the tune keeps the score's tune and its chords and writes every other part
 * again, so there is no count of the score's notes kept and left out to give. What there is to
 * say is who carries the tune and how it stands out from the rest, how many of the tune's chords
 * a voicing was found for (a chord no voicing fits is left to the tune), which players the
 * texture rested and for how many chords, and -- where the AI arranger was on -- how many
 * seat-chords the models rested and set walking out of how many there were. Where the note model
 * was asked for and wrote nothing, the tune was arranged this way instead, and the reason is said
 * first among the notes, which lights the strip.
 *
 * The numbers stay on the strip and the words go behind the same disclosure as the other
 * strip, lit where there is something the verdict does not already say.
 */

import { useState } from "react";
import type { TunedPayload, TunedStaff } from "@app/shared-types";
import { AI_NAMES } from "./AiArrangerMenu";
import { Figure, Problems, Read, Told, WhoPlaysWhat } from "./ReadingParts";
import { graded, plural, sentence } from "../lib/arrange-words";
import { count } from "../lib/format";

/** What one player was given, as a sentence; `total` is the tune's chords. */
function played(staff: TunedStaff, total: number): string {
  if (staff.carries) return `${staff.name} carries the tune.`;
  if (staff.rested === 0) return `${staff.name} plays under the tune throughout.`;
  return `${staff.name} plays under the tune, resting over ${count(staff.rested)} of the ${plural(total, "chord")}.`;
}

export default function TunedReading({ result }: { readonly result: TunedPayload }): JSX.Element {
  const { source, carried, ai } = result;
  const [open, setOpen] = useState(false);
  const verdict = graded(result.verdict);
  const notes = [...new Set(result.problems)];
  const carriers = result.staves.filter((staff) => staff.carries).map((staff) => staff.name);
  const unvoiced = result.total - result.placed;
  const told = [
    ...(result.unwritten === null
      ? []
      : [
          `the note model wrote nothing, so the tune was arranged by the rules with the AI arranger at ${AI_NAMES[ai.level].label} instead: ${result.unwritten}`,
        ]),
    ...source.notes,
    `the tune stands out by ${carried.relief}`,
    ...(carried.lifted === null ? [] : [carried.lifted]),
    ...(carried.forgone === null ? [] : [carried.forgone]),
  ];

  return (
    <div className="shrink-0 border-t border-shell-edge bg-shell-bar">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-2.5 py-1.5">
        <Read source={source} />
        <Figure
          label="Tune"
          value={carriers.length === 0 ? "nobody" : carriers.join(", ")}
          title={`The tune stands out by ${carried.relief}.`}
        />
        <Figure
          label="Chords voiced"
          value={`${count(result.placed)} / ${count(result.total)}`}
          title={
            unvoiced === 0
              ? "A voicing was found for every chord of the tune."
              : `${plural(unvoiced, "chord")} of the tune had no voicing that fits, and ${unvoiced === 1 ? "was" : "were"} left to the tune.`
          }
        />
        {ai.level === "off" ? null : (
          <Figure
            label="AI"
            value={`${AI_NAMES[ai.level].label} · ${count(ai.rested)} rested · ${count(ai.moving)} moving / ${count(ai.cells)}`}
            title={`Of the ${plural(ai.cells, "seat-chord")} beside the tune, the models rested ${count(ai.rested)} and set ${count(ai.moving)} walking.`}
          />
        )}
        <Figure
          label="Verdict"
          value={verdict.value}
          tone={
            notes.length > 0 ? "text-fault" : verdict.judged === 0 ? "text-shell-dim" : "text-clean"
          }
          title={verdict.title}
        />
        <WhoPlaysWhat
          open={open}
          speak={notes.length > 0 || unvoiced > 0 || result.unwritten !== null}
          onOpen={setOpen}
        />
      </div>

      {!open ? null : (
        <div className="max-h-[30vh] space-y-2 overflow-y-auto border-t border-shell-edge px-2.5 py-2 text-[11px] leading-relaxed text-shell-text">
          <p>
            Arranged anew from the tune and its chords: the tune is written as the score has it, and
            every other part is written again under it from the chords. None of the score&apos;s
            other parts is kept; its metre, key, tempi, dynamics and hairpins are.
          </p>
          {result.key === null ? null : <p>· {sentence(result.key.said)}.</p>}

          <ul className="space-y-0.5">
            {result.staves.map((staff) => (
              <li key={staff.partId}>· {played(staff, result.total)}</li>
            ))}
          </ul>

          {unvoiced === 0 ? null : (
            <p className="text-shell-dim">
              · {count(unvoiced)} of the {plural(result.total, "chord")} had no voicing that fits,
              and {unvoiced === 1 ? "was" : "were"} left to the tune.
            </p>
          )}

          <Told told={told} />
          <Problems notes={notes} />
        </div>
      )}
    </div>
  );
}
